import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getImageURL } from '../../utils/api';

function fmtDateTime(value) {
  if (!value) return '';
  return new Date(value).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function getDefaultRange() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
  return { startDate: `${y}-${m}-01`, endDate: `${y}-${m}-${String(lastDay).padStart(2, '0')}` };
}

function PersonAvatar({ name, imagePath }) {
  const [imgError, setImgError] = useState(false);
  const hasImage = imagePath && !imgError;
  // Same fallback convention as Timetable.jsx: placehold.co when there's no valid photo.
  const src = hasImage ? getImageURL(imagePath) : `https://placehold.co/128x128?text=${encodeURIComponent(name || 'Staff')}`;
  return (
    <img
      src={src}
      alt={name}
      onError={() => setImgError(true)}
      className="h-10 w-10 shrink-0 rounded-full border-2 border-white bg-gray-100 object-cover shadow ring-1 ring-gray-200"
    />
  );
}

function RatingStars({ value }) {
  if (value === null || value === undefined) {
    return <span className="text-xs italic text-gray-400">No ratings</span>;
  }
  const rounded = Math.round(value);
  return (
    <span className="inline-flex items-center gap-0.5" title={`${value.toFixed(2)} / 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <svg
          key={i}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          className={`h-4 w-4 ${i <= rounded ? 'text-amber-400' : 'text-gray-200'}`}
          fill="currentColor"
        >
          <path d="M10 1.5l2.6 5.27 5.82.85-4.21 4.1.99 5.8L10 14.9l-5.2 2.62.99-5.8-4.21-4.1 5.82-.85L10 1.5z" />
        </svg>
      ))}
    </span>
  );
}

const RANK_MEDAL = { 1: '🥇', 2: '🥈', 3: '🥉' };

export default function LecturerRatingsReport() {
  const navigate = useNavigate();
  const defaultRange = getDefaultRange();

  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [lecturers, setLecturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axiosInstance.get('/admin/dashboard/lecturer-ratings', {
        params: { startDate, endDate },
      });
      setLecturers(resp?.data?.lecturers || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load lecturer ratings report');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const openDetail = (lecturer) => {
    const params = new URLSearchParams({
      staffId: String(lecturer.staffId),
      staffName: lecturer.staffName || '',
      startDate,
      endDate,
    });
    window.open(`/admin/report/lecturer-ratings/detail?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const openPoster = () => {
    const params = new URLSearchParams({ startDate, endDate });
    window.open(`/admin/report/lecturer-ratings/poster?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  const [downloadingId, setDownloadingId] = useState(null);
  const downloadExcel = async (lecturer) => {
    setDownloadingId(lecturer.staffId);
    try {
      const resp = await axiosInstance.get('/admin/dashboard/lecturer-ratings/detail', {
        params: { staffId: lecturer.staffId, startDate, endDate },
      });
      const ratings = resp?.data?.ratings || [];

      // Anonymized on purpose -- no student name/ID and no batch, just the
      // rating and the comment behind it, per the coordinator's request.
      const headers = ['Rating', 'Subject', 'Description', 'Date'];
      const data = ratings.map((r) => [
        r.rating,
        r.subjectName || '',
        r.reason || '',
        fmtDateTime(r.createdAt),
      ]);

      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);
      worksheet['!cols'] = [{ wch: 8 }, { wch: 22 }, { wch: 60 }, { wch: 20 }];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Ratings');
      const safeName = (lecturer.staffName || `Staff_${lecturer.staffId}`).replace(/[^a-z0-9]+/gi, '_');
      XLSX.writeFile(workbook, `${safeName}_Ratings_${startDate}_to_${endDate}.xlsx`);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to download ratings excel sheet');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <DashboardLayout title="Favorite Lecturer Evaluation">
      <div className="mx-auto max-w-4xl">
        <div className="mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/admin/report/monitor')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Monitor Report
          </button>
          <button
            type="button"
            onClick={openPoster}
            disabled={lecturers.length === 0}
            title="Open a shareable Top 3 banner in a new tab"
            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-500 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5z" />
            </svg>
            Generate Poster
          </button>
        </div>

        <div className="mb-6 border-b border-gray-200 pb-5">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5z" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-gray-900">Favorite Lecturer Evaluation</h1>
              <p className="mt-0.5 text-sm text-gray-500">
                Lecturers ranked by average student rating for the selected date range, highest first.
              </p>
            </div>
          </div>
        </div>

        {/* Date range */}
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-gray-500">Date Range</p>
          <div className="grid grid-cols-12 items-end gap-4">
            <div className="col-span-5">
              <label className="mb-1.5 block text-xs font-semibold text-gray-600">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-800 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div className="col-span-5">
              <label className="mb-1.5 block text-xs font-semibold text-gray-600">End Date</label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-800 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
            </div>
            <div className="col-span-2">
              <button
                type="button"
                onClick={loadReport}
                disabled={loading || !startDate || !endDate}
                className="w-full rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">{error}</div>
        )}

        {/* Ranked list */}
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="grid grid-cols-12 border-b border-gray-200 bg-gray-50 px-5 py-2.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
            <div className="col-span-1 text-center">Rank</div>
            <div className="col-span-4">Lecturer</div>
            <div className="col-span-2 text-center">Ratings</div>
            <div className="col-span-2 text-center">Average Score</div>
            <div className="col-span-3 text-center">Actions</div>
          </div>

          {loading && (
            <div className="flex items-center justify-center gap-2 py-14 text-sm text-gray-400">
              <svg className="h-4 w-4 animate-spin text-amber-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Loading lecturer ratings…
            </div>
          )}

          {!loading && !error && lecturers.length === 0 && (
            <p className="px-6 py-14 text-center text-sm italic text-gray-400">
              No lecturer ratings found for the selected date range.
            </p>
          )}

          {!loading && lecturers.map((lec, idx) => {
            const rank = idx + 1;
            return (
              <div
                key={lec.staffId}
                className="grid grid-cols-12 items-center gap-x-2 border-b border-gray-100 px-5 py-3.5 last:border-0 hover:bg-gray-50/60"
              >
                <div className="col-span-1 text-center">
                  <span className="text-sm font-bold text-gray-400">
                    {RANK_MEDAL[rank] || `#${rank}`}
                  </span>
                </div>
                <div className="col-span-4 flex min-w-0 items-center gap-3">
                  <PersonAvatar name={lec.staffName} imagePath={lec.imagePath} />
                  <p className="truncate text-sm font-semibold text-gray-900">{lec.staffName}</p>
                </div>
                <div className="col-span-2 text-center">
                  <span className="text-sm font-medium text-gray-700">{lec.ratingsCount}</span>
                </div>
                <div className="col-span-2 flex flex-col items-center gap-0.5">
                  <RatingStars value={lec.avgRating} />
                  {lec.avgRating !== null && (
                    <span className="text-xs font-bold text-amber-600">{lec.avgRating.toFixed(2)} / 5</span>
                  )}
                </div>
                <div className="col-span-3 flex justify-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => openDetail(lec)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] font-semibold text-amber-700 transition-colors hover:bg-amber-100"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7S1.274 16.057 2.458 12z" />
                    </svg>
                    Detail
                  </button>
                  <button
                    type="button"
                    onClick={() => downloadExcel(lec)}
                    disabled={downloadingId === lec.staffId}
                    title="Download ratings as Excel (no student or batch details)"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-[11px] font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {downloadingId === lec.staffId ? (
                      <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                      </svg>
                    )}
                    Excel
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
}
